import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import logoUrl from '@/assets/logo_a2a.svg';
import { useAdminAuthGuard } from '@/hooks/useAdminAuthGuard';
import { authService } from '@/services/auth.service';
import { useAuthStore } from '@/store/auth.store';
import { classNames } from '@/utils/classNames';

import { AdminOrdersTable } from './AdminOrdersTable';
import { AdminPricingSection } from './AdminPricingSection.tsx';
import { AdminSiteContentSection } from './AdminSiteContentSection';
import styles from './AdminDashboardPage.module.css';

type AdminTab = 'orders' | 'pricing' | 'siteContent' | 'adminUsers';

function tabFromPathname(pathname: string): AdminTab {
  if (pathname === '/admin/content') {
    return 'siteContent';
  }
  if (
    pathname === '/admin/promocodes' ||
    pathname.startsWith('/admin/promocodes/') ||
    pathname === '/admin/pricing' ||
    pathname === '/admin/pricing/markup' ||
    pathname === '/admin/pricing/processors'
  ) {
    return 'pricing';
  }
  return 'orders';
}

export const AdminDashboardPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<AdminTab>(() => tabFromPathname(location.pathname));
  const { isChecking } = useAdminAuthGuard();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);

  useEffect(() => {
    setActiveTab(tabFromPathname(location.pathname));
  }, [location.pathname]);

  const handleLogout = () => {
    authService.logout().catch(() => undefined);
    logout();
  };

  if (isChecking) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <img src={logoUrl} alt="Ski Pass" className={styles.logo} />
          <div className={styles.tabs}>
            <button
              type="button"
              className={classNames(
                styles.tab,
                activeTab === 'orders' ? styles.tabActive : styles.tabMuted,
              )}
              onClick={() => {
                setActiveTab('orders');
                void navigate('/admin');
              }}
            >
              Orders
            </button>
            <button
              type="button"
              className={classNames(
                styles.tab,
                activeTab === 'pricing' ? styles.tabActive : styles.tabMuted,
              )}
              onClick={() => {
                setActiveTab('pricing');
                void navigate('/admin/promocodes');
              }}
            >
              Pricing
            </button>
            <button
              type="button"
              className={classNames(
                styles.tab,
                activeTab === 'siteContent' ? styles.tabActive : styles.tabMuted,
              )}
              onClick={() => {
                setActiveTab('siteContent');
                void navigate('/admin/content');
              }}
            >
              Content
            </button>
            <button
              type="button"
              className={classNames(
                styles.tab,
                activeTab === 'adminUsers' ? styles.tabActive : styles.tabMuted,
              )}
              onClick={() => {
                setActiveTab('adminUsers');
              }}
            >
              Admin users
            </button>
          </div>
        </div>

        <div className={styles.userSection}>
          <span className={styles.userName}>{user?.name}</span>
          <button
            type="button"
            onClick={handleLogout}
            className={styles.logoutButton}
            aria-label="Logout"
            title="Logout"
          >
            ⎋
          </button>
        </div>
      </header>

      <section className={styles.section}>
        {activeTab === 'orders' ? <AdminOrdersTable /> : null}
        {activeTab === 'pricing' ? <AdminPricingSection /> : null}
        {activeTab === 'siteContent' ? <AdminSiteContentSection /> : null}
        {activeTab === 'adminUsers' ? (
          <div className={styles.placeholder}>Admin users management UI will be added next.</div>
        ) : null}
      </section>
    </div>
  );
};
